import * as React from "react";
import type { ChatAvailableModel } from "../../types/chat";
import { ChatSelectedTextStrip } from "./ChatSelectedTextStrip";
import { IconPlus, IconSend } from "./icons/ChatIcons";

export type ChatComposerProps = {
  inputValue: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  sendBusy: boolean;
  selectedText: string | null;
  onClearSelection: () => void;
  referenceKbFileLabels: Array<{ id: string; fileName: string }>;
  onOpenKbContext: () => void;
  chatModel: string;
  onChatModelChange: (id: string) => void;
  availableModels: ChatAvailableModel[];
  modelsLoading: boolean;
  modelsLoaded: boolean;
  modelsError: string | null;
  active: boolean;
  /** When false, composer is non-interactive (e.g. missing ``assistant:use``). */
  enabled?: boolean;
  disabledReason?: string | null;
  inputRef?: React.RefObject<HTMLTextAreaElement | null>;
};

export const ChatComposer: React.FC<ChatComposerProps> = ({
  inputValue,
  onInputChange,
  onSend,
  sendBusy,
  selectedText,
  onClearSelection,
  referenceKbFileLabels,
  onOpenKbContext,
  chatModel,
  onChatModelChange,
  availableModels,
  modelsLoading,
  modelsLoaded,
  modelsError,
  active,
  enabled = true,
  disabledReason = null,
  inputRef,
}) => {
  const canSend = enabled && Boolean(inputValue.trim()) && !sendBusy;

  const resizeTextarea = (el: HTMLTextAreaElement) => {
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  return (
    <div className={`chat-composer-wrap${enabled ? "" : " is-disabled"}`}>
      {selectedText && enabled ? (
        <ChatSelectedTextStrip text={selectedText} onClear={onClearSelection} />
      ) : null}

      <div className="cenna-composer" aria-disabled={!enabled}>
        <textarea
          ref={inputRef}
          className="cenna-composer__textarea"
          value={inputValue}
          onChange={(e) => {
            if (!enabled) return;
            onInputChange(e.target.value);
            resizeTextarea(e.target);
          }}
          onKeyDown={(e) => {
            if (!enabled) return;
            if (e.key !== "Enter" || e.shiftKey) return;
            e.preventDefault();
            if (canSend) onSend();
          }}
          placeholder={enabled ? "Message Cenna…" : "Ask Cenna unavailable"}
          rows={2}
          disabled={!enabled}
          readOnly={!enabled}
        />

        {enabled && referenceKbFileLabels.length > 0 ? (
          <div className="cenna-composer__kb">
            <span className="cenna-composer__kb-label">Knowledge base context</span>
            <ul className="cenna-composer__kb-list">
              {referenceKbFileLabels.map((f) => (
                <li key={f.id}>{f.fileName}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="cenna-composer__toolbar">
          <button
            type="button"
            className="cenna-composer__add-ctx"
            onClick={onOpenKbContext}
            aria-label="Add knowledge base context"
            disabled={!enabled}
          >
            <IconPlus size={11} />
            Add context
          </button>

          {enabled && availableModels.length > 0 ? (
            <select
              className="cenna-composer__model"
              value={chatModel || undefined}
              onChange={(e) => onChatModelChange(e.target.value)}
              aria-label="Model"
              disabled={modelsLoading || availableModels.length === 0}
            >
              {availableModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.provider ? `${m.label} (${m.provider})` : m.label}
                </option>
              ))}
            </select>
          ) : enabled && modelsLoading ? (
            <span className="cenna-composer__model">Loading models…</span>
          ) : enabled ? (
            <span className="cenna-composer__model">Select a model</span>
          ) : null}

          <span className="cenna-composer__spacer" />

          <button
            type="button"
            className="cenna-send"
            aria-label="Send"
            title={enabled ? "Send" : disabledReason || "Ask Cenna unavailable"}
            disabled={!canSend}
            onClick={onSend}
          >
            <IconSend size={12} />
          </button>
        </div>
      </div>

      {!enabled && disabledReason ? (
        <div className="chat-composer__banner" role="status">
          {disabledReason}
        </div>
      ) : null}

      {enabled && active && modelsLoaded && !modelsLoading && !modelsError && availableModels.length === 0 ? (
        <div className="chat-composer__banner" role="status">
          No models configured. Please configure models for chat on the platform.
        </div>
      ) : null}

      {enabled && active && modelsError ? (
        <div className="chat-composer__banner" role="alert">
          {modelsError}
        </div>
      ) : null}
    </div>
  );
};
